// Sidebar.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config/ApiConfig';
import useDefaultModal from './hooks/useDefaultModal';
import DefaultModal from './modals/DefaultModal';
import LoadingModal from './modals/LoadingModal';
import useAsyncPdf from './hooks/useAsyncPdf';
import '../css/Sidebar.css';

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState(() => {
    const stored = localStorage.getItem('sidebarExpandedMenus');
    return stored ? JSON.parse(stored) : {};
  });

  // (removed specific admin-only collapse effect) — geral será tratado abaixo após a definição de menuItems
  const [vinculo] = useState(() => {
    const storedVinculo = localStorage.getItem('vinculo');
    return storedVinculo ? JSON.parse(storedVinculo) : null;
  });
  const [user] = useState(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const generatePdf = useAsyncPdf({ token: localStorage.getItem('token') });

  const isActive = path => {
    return location.pathname === path;
  };

  const toggleSubmenu = index => {
    // Se o sidebar estiver fechado, abre ele primeiro
    if (!isOpen) {
      toggleSidebar();
    }
    setExpandedMenus(prev => {
      const isOpening = !prev[index];
      let updated = {};
      if (isOpening) {
        // abrir apenas o submenu clicado, fechando os demais
        updated = { [index]: true };
      } else {
        // fechar todos
        updated = {};
      }
      localStorage.setItem('sidebarExpandedMenus', JSON.stringify(updated));
      return updated;
    });
  };

  const handleNavigation = path => {
    navigate(path);
    if (window.innerWidth < 992) {
      toggleSidebar();
    }
  };

  const handleGerarRelatorioContratos = async () => {
    if (!vinculo?.idEntidade) {
      showDefaultModal('error', 'Vínculo não encontrado. Por favor, selecione uma entidade.');
      return;
    }

    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    setIsGeneratingReport(true);
    try {
      const { blob } = await generatePdf({
        startFn: () => axios.post(
          `${API_BASE_URL.CONTRATO}/listaContratosPdf/${vinculo.idEntidade}/${anoBase}`,
          {},
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        ),
        statusUrlFn: jobId => `${API_BASE_URL.CONTRATO}/listaContratosPdfStatus/${jobId}`,
        fileUrlFn: jobId => `${API_BASE_URL.CONTRATO}/listaContratosPdfFile/${jobId}`,
      });

      const url = window.URL.createObjectURL(blob);
      const pdfId = `contratos_${vinculo.idEntidade}_${anoBase}_${Date.now()}`;

      if (!window.pdfStorage) window.pdfStorage = {};
      window.pdfStorage[pdfId] = blob;

      sessionStorage.setItem('pdfViewerData', JSON.stringify({ url, title: `Relação de Contratos - ${anoBase}`, pdfId }));
      window.dispatchEvent(new CustomEvent('pdfViewerUpdate', { detail: { pdfId } }));

      setIsGeneratingReport(false);
      if (window.innerWidth < 992) toggleSidebar();
    } catch (error) {
      setIsGeneratingReport(false);
      console.error('Erro ao gerar relatório:', error);
      showDefaultModal('error', error.message || 'Não há contratos para o ano selecionado.');
    }
  };

  const handleGerarRelatorioExecucao = async () => {
    if (!vinculo?.idEntidade) {
      showDefaultModal('error', 'Vínculo não encontrado. Por favor, selecione uma entidade.');
      return;
    }

    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    setIsGeneratingReport(true);
    try {
      const { blob } = await generatePdf({
        startFn: () => axios.post(
          `${API_BASE_URL.CONTRATO}/listaExecucaoPdf/${vinculo.idEntidade}/${anoBase}`,
          {},
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        ),
        statusUrlFn: jobId => `${API_BASE_URL.CONTRATO}/listaExecucaoPdfStatus/${jobId}`,
        fileUrlFn: jobId => `${API_BASE_URL.CONTRATO}/listaExecucaoPdfFile/${jobId}`,
      });

      const url = window.URL.createObjectURL(blob);
      const pdfId = `execucao_${vinculo.idEntidade}_${anoBase}_${Date.now()}`;

      if (!window.pdfStorage) window.pdfStorage = {};
      window.pdfStorage[pdfId] = blob;

      sessionStorage.setItem('pdfViewerData', JSON.stringify({ url, title: `Relatório de Execução Contratual - ${anoBase}`, pdfId }));
      window.dispatchEvent(new CustomEvent('pdfViewerUpdate', { detail: { pdfId } }));

      setIsGeneratingReport(false);
      if (window.innerWidth < 992) toggleSidebar();
    } catch (error) {
      setIsGeneratingReport(false);
      console.error('Erro ao gerar relatório:', error);
      showDefaultModal('error', error.message || 'Não há contratos para o ano selecionado.');
    }
  };

  const handleGerarListaArps = async () => {
    if (!vinculo?.idEntidade) {
      showDefaultModal('error', 'Vínculo não encontrado. Por favor, selecione uma entidade.');
      return;
    }

    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    setIsGeneratingReport(true);
    try {
      const { blob } = await generatePdf({
        startFn: () => axios.post(
          `${API_BASE_URL.ARP}/listaArpsPdf/${vinculo.idEntidade}/${anoBase}`,
          {},
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        ),
        statusUrlFn: jobId => `${API_BASE_URL.ARP}/listaArpsPdfStatus/${jobId}`,
        fileUrlFn: jobId => `${API_BASE_URL.ARP}/listaArpsPdfFile/${jobId}`,
      });

      const url = window.URL.createObjectURL(blob);
      const pdfId = `arps_${vinculo.idEntidade}_${anoBase}_${Date.now()}`;
      if (!window.pdfStorage) window.pdfStorage = {};
      window.pdfStorage[pdfId] = blob;
      sessionStorage.setItem('pdfViewerData', JSON.stringify({ url, title: `Lista de ARPs - ${anoBase}`, pdfId }));
      window.dispatchEvent(new CustomEvent('pdfViewerUpdate', { detail: { pdfId } }));
      setIsGeneratingReport(false);
      if (window.innerWidth < 992) toggleSidebar();
    } catch (error) {
      setIsGeneratingReport(false);
      console.error('Erro ao gerar relatório ARPs (lista):', error);
      showDefaultModal('error', error.message || 'O relatório não foi gerado!');
    }
  };

  const handleGerarExecucaoArps = async () => {
    if (!vinculo?.idEntidade) {
      showDefaultModal('error', 'Vínculo não encontrado. Por favor, selecione uma entidade.');
      return;
    }

    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();

    setIsGeneratingReport(true);
    try {
      const { blob } = await generatePdf({
        startFn: () => axios.post(
          `${API_BASE_URL.ARP}/listaExecucaoArpsPdf/${vinculo.idEntidade}/${anoBase}`,
          {},
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        ),
        statusUrlFn: jobId => `${API_BASE_URL.ARP}/listaExecucaoArpsPdfStatus/${jobId}`,
        fileUrlFn: jobId => `${API_BASE_URL.ARP}/listaExecucaoArpsPdfFile/${jobId}`,
      });

      const url = window.URL.createObjectURL(blob);
      const pdfId = `execucao_arps_${vinculo.idEntidade}_${anoBase}_${Date.now()}`;
      if (!window.pdfStorage) window.pdfStorage = {};
      window.pdfStorage[pdfId] = blob;
      sessionStorage.setItem('pdfViewerData', JSON.stringify({ url, title: `Execução ARPs - ${anoBase}`, pdfId }));
      window.dispatchEvent(new CustomEvent('pdfViewerUpdate', { detail: { pdfId } }));
      setIsGeneratingReport(false);
      if (window.innerWidth < 992) toggleSidebar();
    } catch (error) {
      setIsGeneratingReport(false);
      console.error('Erro ao gerar relatório ARPs (execução):', error);
      showDefaultModal('error', error.message || 'O relatório não foi gerado!');
    }
  };

  const handleCorrigirDatasPreEmpenho = async () => {
    const token = localStorage.getItem('token');
    setIsGeneratingReport(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL.PRE_EMPENHO}/corrigir-datas`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { total, corrigidos } = response.data;
      setIsGeneratingReport(false);
      showDefaultModal('success', `Correção concluída!\nTotal de pré-empenhos: ${total}\nCorrigidos: ${corrigidos}`);
      if (window.innerWidth < 992) toggleSidebar();
    } catch (error) {
      setIsGeneratingReport(false);
      console.error('Erro ao corrigir datas:', error);
      showDefaultModal('error', 'Erro ao corrigir datas dos pré-empenhos.');
    }
  };

  const menuItems = [
    {
      title: 'Home',
      icon: 'bi-house-door',
      path: '/home',
      show: true,
    },
    {
      title: 'Cadastros',
      icon: 'bi-card-list',
      // show: vinculo?.tipo === 'ADMIN' && user?.role === 'ADMIN',
      show: true,
      submenu: [
        {
          title: 'Ações Governamentais',
          icon: 'bi-diagram-3',
          path: '/acao-governamental',
        },
        {
          title: 'Descrição Entrega',
          icon: 'bi-card-text',
          path: '/descricao-entrega',
        },
        {
          title: 'Fontes de Recursos',
          icon: 'bi-currency-dollar',
          path: '/fonte-recurso',
        },
        {
          title: 'Fornecedores',
          icon: 'bi-shop',
          path: '/fornecedores',
        },
      ],
    },
    {
      title: 'ARPs',
      icon: 'bi-journal-bookmark',
      path: '/arps',
      show: true,
    },
    {
      title: 'Contratos',
      icon: 'bi-file-earmark-text',
      path: '/contratos',
      show: true,
    },
    {
      title: 'Compras Diretas',
      icon: 'bi-cart',
      path: '/compras-direta',
      show: true,
    },
    {
      title: 'Relatórios',
      icon: 'bi-file-earmark-bar-graph',
      show: true,
      submenu: [
        {
          title: 'Relação de Contratos',
          icon: 'bi-file-text',
          action: handleGerarRelatorioContratos,
        },
        {
          title: 'Relação de ARPs',
          icon: 'bi-file-text',
          action: handleGerarListaArps,
        },
        {
          title: 'Execução Contratual',
          icon: 'bi-clipboard-check',
          action: handleGerarRelatorioExecucao,
        },
        {
          title: 'Execução ARPs',
          icon: 'bi-clipboard-check',
          action: handleGerarExecucaoArps,
        },
      ],
    },
    
    {
      title: 'Administração',
      icon: 'bi-gear',
      show: vinculo?.tipo === 'ADMIN' && user?.role === 'ADMIN',
      submenu: [
        {
          title: 'Entidades',
          icon: 'bi-building',
          path: '/entidades',
        },
        {
          title: 'Usuários',
          icon: 'bi-people',
          path: '/usuarios',
        },
        {
          title: 'Imagens',
          icon: 'bi-image',
          path: '/imagens',
        },
        {
          title: 'Sessões Ativas',
          icon: 'bi-list-task',
          path: '/sessions-ativas',
        },
        {
          title: 'Elementos Despesa',
          icon: 'bi-list-ul',
          path: '/elemento-despesa',
        },
        {
          title: 'Órgãos Emissores',
          icon: 'bi-file-earmark-code',
          path: '/emissores',
        },
        {
          title: 'Bancos',
          icon: 'bi-bank',
          path: '/bancos',
        },
        {
          title: 'Naturezas Jurídicas',
          icon: 'bi-building',
          path: '/natureza-juridica',
        },
        {
          title: 'Corrigir Datas Pré-Empenho',
          icon: 'bi-calendar-check',
          action: handleCorrigirDatasPreEmpenho,
        },
        {
          title: 'Parâmetros por Entidade',
          icon: 'bi-sliders',
          path: '/parametros-entidade',
        },
      ],
    },
  ];

  // Fecha submenus que não contenham a rota atual. Mantém aberto somente o submenu cujo subitem corresponde à rota.
  useEffect(() => {
    let keepIndex = null;
    menuItems.forEach((item, idx) => {
      if (item.submenu) {
        const match = item.submenu.some(si => si.path && location.pathname.startsWith(si.path));
        if (match) keepIndex = idx;
      }
    });

    if (keepIndex !== null) {
      const updated = { [keepIndex]: true };
      setExpandedMenus(prev => {
        if (JSON.stringify(prev) === JSON.stringify(updated)) return prev;
        localStorage.setItem('sidebarExpandedMenus', JSON.stringify(updated));
        return updated;
      });
    } else {
      // fechar todos se nenhuma correspondência
      setExpandedMenus(prev => {
        if (!prev || Object.keys(prev).length === 0) return prev;
        localStorage.setItem('sidebarExpandedMenus', JSON.stringify({}));
        return {};
      });
    }
  }, [location.pathname]);

  return (
    <>
      {/* Overlay para mobile */}
      {isOpen && <div className="sidebar-overlay" onClick={toggleSidebar} />}

      {/* Sidebar */}
      <aside className={`modern-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <button
            className="btn-close-sidebar d-lg-none"
            onClick={toggleSidebar}
            aria-label="Fechar menu"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <nav className="sidebar-nav">
          <ul className="sidebar-menu">
            {menuItems.map((item, index) => {
              if (!item.show) return null;

              // Destacar menus quando estiver em suas páginas
              const adminPaths = [
                '/entidade',
                '/entidades',
                '/usuario',
                '/usuarios',
                '/vinculos',
                '/imagens',
                '/sessions-ativas',
                '/natureza-juridica',
                '/parametros-entidade',
              ];
              
              const isAdminMenu =
                item.title === 'Administração' &&
                adminPaths.some(p => location.pathname.startsWith(p));
              
              const isMenuActive = isAdminMenu;

              if (item.submenu) {
                return (
                  <li
                    key={index}
                    className={`sidebar-item has-submenu${
                      isMenuActive ? ' active' : ''
                    }`}
                  >
                    <button
                      className={`sidebar-link ${
                        expandedMenus[index] ? 'expanded' : ''
                      }${isMenuActive ? ' active' : ''}`}
                      onClick={() => toggleSubmenu(index)}
                      title={!isOpen ? item.title : ''}
                    >
                      <div className="sidebar-link-content">
                        <i className={`bi ${item.icon}`}></i>
                        {!isOpen && <span className="sidebar-label-collapsed">{item.title}</span>}
                      </div>
                      {isOpen && (
                        <>
                          <span>{item.title}</span>
                          <i
                            className={`bi bi-chevron-${
                              expandedMenus[index] ? 'down' : 'right'
                            } ms-auto submenu-arrow`}
                          ></i>
                        </>
                      )}
                    </button>
                    {isOpen && expandedMenus[index] && (
                      <ul className="sidebar-submenu" style={item.title === 'Administração' ? { maxHeight: '280px', overflowY: 'auto' } : {}}>
                        {item.submenu.map((subitem, subindex) => (
                          <li key={subindex}>
                            <button
                              className={`sidebar-sublink ${
                                subitem.path && isActive(subitem.path) ? 'active' : ''
                              }`}
                              onClick={() => {
                                if (subitem.action) {
                                  subitem.action();
                                } else if (subitem.path) {
                                  handleNavigation(subitem.path);
                                }
                              }}
                              title={subitem.title}
                            >
                              <i className={`bi ${subitem.icon}`}></i>
                              <span>{subitem.title}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              }

              return (
                <li
                  key={index}
                  className={`sidebar-item${
                    isActive(item.path) ? ' active' : ''
                  }`}
                >
                  <button
                    className={`sidebar-link${
                      isActive(item.path) ? ' active' : ''
                    }`}
                    onClick={() => handleNavigation(item.path)}
                    title={!isOpen ? item.title : ''}
                  >
                    <div className="sidebar-link-content">
                      <i className={`bi ${item.icon}`}></i>
                      {!isOpen && <span className="sidebar-label-collapsed">{item.title}</span>}
                    </div>
                    {isOpen && <span>{item.title}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>
      <DefaultModal {...setDefaultModal} />
      <LoadingModal loading={isGeneratingReport} message="Gerando relatório, por favor aguarde..." />
    </>
  );
};

export default Sidebar;
